import { BookOpenIcon, PlusIcon } from '@heroicons/react/24/outline'
import { UserGuard } from '@reader-tracker/shared/core/components/UserGuard'
import { supabase } from '@reader-tracker/shared/lib/supabase'
import { useAuthStore } from '@reader-tracker/shared/stores/authStore'
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import type { LibraryItem, LibraryProgress } from './interfaces'

function LibraryContent() {
  const user = useAuthStore((s) => s.user)
  const [items, setItems] = useState<LibraryItem[]>([])
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    if (!user) return

    async function fetchLibrary() {
      setIsLoading(true)
      const { data: follows } = await supabase
        .from('follows')
        .select('title_id, created_at, titles(*)')
        .order('created_at', { ascending: false })

      if (!follows) {
        setIsLoading(false)
        return
      }

      const titleIds = follows.map((f) => f.title_id)

      const { data: progressRows } = titleIds.length
        ? await supabase
            .from('progress')
            .select('title_id, page, updated_at, chapters(*)')
            .in('title_id', titleIds)
        : { data: [] }

      const progressMap = new Map(
        progressRows?.map((p) => [p.title_id, p]) ?? [],
      )

      const libraryItems: LibraryItem[] = follows.map((f) => {
        const prog = progressMap.get(f.title_id)
        return {
          title: f.titles as unknown as LibraryItem['title'],
          followedAt: f.created_at,
          progress: prog
            ? {
                chapter: prog.chapters as unknown as LibraryProgress['chapter'],
                page: prog.page,
                updatedAt: prog.updated_at,
              }
            : null,
        }
      })

      setItems(libraryItems)
      setIsLoading(false)
    }

    fetchLibrary()
  }, [user])

  async function handleUnfollow(titleId: string) {
    await supabase.from('follows').delete().eq('title_id', titleId)
    setItems((prev) => prev.filter((i) => i.title.id !== titleId))
  }

  if (isLoading) {
    return (
      <div className="flex justify-center py-20">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-blue-600 border-t-transparent" />
      </div>
    )
  }

  if (items.length === 0) {
    return (
      <div className="flex flex-col items-center gap-4 py-24 text-center text-gray-500">
        <BookOpenIcon className="h-16 w-16 text-gray-300" />
        <p className="font-medium text-lg">Your library is empty</p>
        <p className="text-sm">
          Search for titles and follow them to track your progress.
        </p>
        <Link
          to="/search"
          className="mt-2 inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm text-white hover:bg-blue-700"
        >
          <PlusIcon className="h-4 w-4" />
          Browse titles
        </Link>
      </div>
    )
  }

  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
      {items.map(({ title, progress }) => (
        <div
          key={title.id}
          className="flex flex-col overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm transition-shadow hover:shadow-md"
        >
          <Link
            to={`/title/${title.id}`}
            className="relative block aspect-[2/3] overflow-hidden bg-gray-100"
          >
            {title.cover_url ? (
              <img
                src={title.cover_url}
                alt={title.canonical_name}
                className="h-full w-full object-cover"
                loading="lazy"
              />
            ) : (
              <div className="flex h-full items-center justify-center">
                <BookOpenIcon className="h-12 w-12 text-gray-300" />
              </div>
            )}
            <span
              className={`absolute top-2 left-2 rounded-full px-2 py-0.5 font-medium text-white text-xs ${
                title.status === 'completed'
                  ? 'bg-green-600'
                  : title.status === 'hiatus'
                    ? 'bg-yellow-600'
                    : 'bg-blue-600'
              }`}
            >
              {title.status}
            </span>
          </Link>

          <div className="flex flex-1 flex-col gap-2 p-3">
            <Link
              to={`/title/${title.id}`}
              className="line-clamp-2 font-semibold text-gray-900 text-sm hover:text-blue-600"
            >
              {title.canonical_name}
            </Link>

            <span className="self-start rounded bg-gray-100 px-2 py-0.5 text-gray-600 text-xs capitalize">
              {title.type}
            </span>

            {progress ? (
              <div className="mt-auto flex flex-col gap-1">
                <p className="text-gray-500 text-xs">
                  Last read: Ch.{progress.chapter.chapter_no}
                  {progress.chapter.title ? ` — ${progress.chapter.title}` : ''}
                </p>
                <Link
                  to={`/read/${progress.chapter.id}`}
                  className="inline-flex items-center justify-center gap-1 rounded-lg bg-blue-600 px-3 py-1.5 text-sm text-white hover:bg-blue-700"
                >
                  <BookOpenIcon className="h-4 w-4" />
                  Continue reading
                </Link>
              </div>
            ) : (
              <div className="mt-auto">
                <Link
                  to={`/title/${title.id}`}
                  className="inline-flex w-full items-center justify-center gap-1 rounded-lg border border-blue-600 px-3 py-1.5 text-blue-600 text-sm hover:bg-blue-50"
                >
                  Start reading
                </Link>
              </div>
            )}

            <button
              type="button"
              onClick={() => handleUnfollow(title.id)}
              className="text-center text-gray-400 text-xs hover:text-red-500"
            >
              Unfollow
            </button>
          </div>
        </div>
      ))}
    </div>
  )
}

export default function LibraryPage() {
  return (
    <UserGuard>
      <div className="mx-auto max-w-7xl px-4 py-8">
        <div className="mb-6 flex items-center justify-between">
          <h1 className="font-bold text-2xl text-gray-900">My Library</h1>
          <Link
            to="/search"
            className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm text-white hover:bg-blue-700"
          >
            <PlusIcon className="h-4 w-4" />
            Add titles
          </Link>
        </div>
        <LibraryContent />
      </div>
    </UserGuard>
  )
}
